import { FileText, Download, Printer, CheckCircle2, X } from 'lucide-react';
import { BillInvoice, TariffPlan, UserAccount } from '../types/energy';
import { Language, translations } from '../i18n/translations';

interface InvoiceModalProps {
  invoice: BillInvoice | null;
  account: UserAccount;
  tariff: TariffPlan;
  onClose: () => void;
  onPayNow?: (invoice: BillInvoice) => void;
  lang?: Language;
}

export function InvoiceModal({
  invoice,
  account,
  tariff,
  onClose,
  onPayNow,
  lang = 'ja'
}: InvoiceModalProps) {
  if (!invoice) return null;

  const t = translations[lang];

  const handlePrint = () => {
    window.print();
  };

  const handleDownload = () => {
    const dataStr = `data:text/json;charset=utf-8,` + encodeURIComponent(JSON.stringify(invoice, null, 2));
    const downloadAnchor = document.createElement('a');
    downloadAnchor.setAttribute('href', dataStr);
    downloadAnchor.setAttribute('download', `${invoice.invoiceNumber}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md animate-fadeIn">
      <div className="w-full max-w-lg bg-slate-900 border border-slate-800 rounded-3xl p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto">
        {/* Modal Top Bar */}
        <div className="flex items-center justify-between pb-4 border-b border-slate-800">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-teal-500/15 border border-teal-500/30 text-teal-400 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-base font-bold text-white">{t.statementModalTitle}</h3>
              <p className="text-xs text-slate-400 font-mono">{invoice.invoiceNumber}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white flex items-center justify-center transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Paper Statement Container */}
        <div className="my-5 bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-5 text-slate-200 text-xs">
          {/* Header Info */}
          <div className="flex justify-between items-start">
            <div>
              <span className="text-base font-black tracking-wider text-teal-400">{t.corpName}</span>
              <p className="text-[11px] text-slate-400 mt-0.5">{t.corpSub}</p>
              <p className="text-[10px] text-slate-500">{t.corpRegNo}</p>
              <p className="text-[10px] text-slate-500">{t.corpAddress}</p>
            </div>
            <div className="text-right">
              <span
                className={`inline-block px-2.5 py-1 text-[11px] font-bold rounded-lg ${
                  invoice.status === 'paid'
                    ? 'bg-emerald-950/80 text-emerald-400 border border-emerald-800/60'
                    : 'bg-rose-950/80 text-rose-400 border border-rose-800/60'
                }`}
              >
                {invoice.status === 'paid' ? t.statusPaidBadge : t.statusDueBadge}
              </span>
              <p className="text-[11px] text-slate-400 mt-1 font-mono">{t.paidOn.replace('{date}', invoice.issueDate)}</p>
              <p className="text-[11px] text-slate-400 font-mono">{t.dueOn.replace('{date}', invoice.dueDate)}</p>
            </div>
          </div>

          {/* Account Details Box */}
          <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-3 grid grid-cols-2 gap-2">
            <div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t.customerNameLabel}</span>
              <p className="font-semibold text-white mt-0.5">{account.accountHolder}</p>
              <p className="text-[11px] text-slate-400 truncate">{account.serviceAddress}</p>
            </div>
            <div>
              <span className="text-[10px] text-slate-400 uppercase tracking-wider">{t.accountSupplyPointLabel}</span>
              <p className="font-mono text-white font-medium mt-0.5">{account.accountNumber}</p>
              <p className="text-[10px] text-slate-400 font-mono truncate">{tariff.mprnNumber}</p>
            </div>
          </div>

          {/* Billing Period & Consumption */}
          <div className="border-t border-slate-800/80 pt-3 flex justify-between items-center">
            <div>
              <p className="font-medium text-white">
                {t.billingPeriodText.replace('{start}', invoice.periodStart).replace('{end}', invoice.periodEnd)}
              </p>
            </div>
            <div className="text-right">
              <p className="font-bold text-teal-400 font-mono">
                {t.totalUsageText.replace('{kwh}', invoice.kwhUsed.toString())}
              </p>
            </div>
          </div>

          {/* Line Item Table (No solar credit) */}
          <div className="border-t border-slate-800/80 pt-3">
            <table className="w-full text-left">
              <thead>
                <tr className="text-slate-400 text-[10px] uppercase border-b border-slate-800 pb-1">
                  <th className="py-1">{t.descColumn}</th>
                  <th className="py-1 text-right">{t.amountColumn}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/50">
                <tr>
                  <td className="py-2">{t.itemBaseCharge}</td>
                  <td className="py-2 text-right font-mono">¥{invoice.breakdown.standingCharges.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-2">{t.itemDayUsage.replace('{rate}', tariff.unitRateDay.toFixed(2))}</td>
                  <td className="py-2 text-right font-mono">¥{invoice.breakdown.electricityDay.toLocaleString()}</td>
                </tr>
                {invoice.breakdown.electricityLife !== undefined && (
                  <tr>
                    <td className="py-2">{t.itemLifeUsage.replace('{rate}', (tariff.unitRateLife || 32.65).toFixed(2))}</td>
                    <td className="py-2 text-right font-mono">¥{invoice.breakdown.electricityLife.toLocaleString()}</td>
                  </tr>
                )}
                <tr>
                  <td className="py-2">{t.itemNightUsage.replace('{rate}', tariff.unitRateNight.toFixed(2))}</td>
                  <td className="py-2 text-right font-mono">¥{invoice.breakdown.electricityNight.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-2">{t.itemRenewableLevy}</td>
                  <td className="py-2 text-right font-mono">¥{invoice.breakdown.renewableLevy.toLocaleString()}</td>
                </tr>
                <tr>
                  <td className="py-2">{t.itemTax}</td>
                  <td className="py-2 text-right font-mono">¥{invoice.breakdown.taxes.toLocaleString()}</td>
                </tr>
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-700 text-sm font-bold text-white">
                  <td className="py-2.5">{t.totalDueRow}</td>
                  <td className="py-2.5 text-right font-mono text-teal-400">¥{invoice.totalAmount.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2.5 pt-2">
          {invoice.status === 'due' && onPayNow && (
            <button
              type="button"
              onClick={() => {
                onPayNow(invoice);
                onClose();
              }}
              className="flex-1 h-11 bg-teal-500 hover:bg-teal-400 active:scale-[0.98] text-slate-950 font-bold text-xs rounded-xl flex items-center justify-center gap-2 transition-transform shadow-lg shadow-teal-500/20"
            >
              <CheckCircle2 className="w-4 h-4" />
              {t.payStatementBtn.replace('{val}', invoice.totalAmount.toLocaleString())}
            </button>
          )}

          <button
            type="button"
            onClick={handleDownload}
            className="flex-1 h-11 bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-white font-medium text-xs rounded-xl flex items-center justify-center gap-2 transition-colors border border-slate-700/60"
          >
            <Download className="w-4 h-4 text-slate-300" />
            {t.downloadJsonBtn}
          </button>

          <button
            type="button"
            onClick={handlePrint}
            className="h-11 px-4 bg-slate-800 hover:bg-slate-700 active:bg-slate-600 text-slate-300 hover:text-white font-medium text-xs rounded-xl flex items-center justify-center gap-1.5 transition-colors border border-slate-700/60"
          >
            <Printer className="w-4 h-4" />
            {t.printBtn}
          </button>
        </div>
      </div>
    </div>
  );
}
